<template>
  <b-container fluid class="bg-white">
    <div class="row" v-if="!loading">
      <div class="col-lg lizard-form-empty-col"></div>
    </div>
    <div class="row" v-if="!loading">
      <filters-bar
        :range-enums="rangeEnums"
        :remote-filters="filters"
        :team-members="teamMembers"
        :project-list="projectList"
        :categories-list="categoriesList"
        :user-changed="userChanged"
        :project-changed="projectChanged"
        :category-changed="categoryChanged"
        :reset-filters="resetFilters" />
    </div>
    <div class="row" v-if="!loading">
      <div class="col-lg lizard-form-empty-col"></div>
    </div>
    <div class="row" v-if="!loading">
      <reports-box
        :expenseStatusOpts="expenseStatusOpts"
        :remote-filters="filters"
        :range-enums="rangeEnums"
        :user-changed="userChanged"
        :project-changed="projectChanged" />
    </div>
    <div class="row" v-if="listItems.length">
      <div class="col-lg-12">
        <b-table
          caption-top
          responsive
          fixed
          striped
          small
          borderless
          selectable
          select-mode="range"
          selected-variant="info"
          @row-selected="rowsSelected"
          ref="expensesTable"
          table-class="lizard-activity-list lizard-expenses-box"
          thead-class="label"
          tbody-class="value"
          variant="secondary"
          :items="listItems"
          :fields="listFields"
          :busy="loading">
          <template #table-caption
            ><b-badge
              variant="light"
              style="
                background-color: transparent;
                color: #999;
                text-transform: uppercase;
                font-size: 8pt;
              ">
              DISPLAYING {{ Pager.total }} RECORDS MATCHING SELECTED FILTERS
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <div v-if="data.item.fieldsOnEdit && data.item.fieldsOnEdit.length > 0" class="d-flex align-items-center">
               <b-button size="sm" variant="success" class="mr-1" style="padding: 0.15rem 0.3rem;" @click="saveRowEdits(data.item)" title="Save changes">
                 <b-icon icon="check"></b-icon>
               </b-button>
               <b-button size="sm" variant="danger" style="padding: 0.15rem 0.3rem;" @click="cancelRowEdits(data.item)" title="Cancel changes">
                 <b-icon icon="x"></b-icon>
               </b-button>
            </div>
          </template>
          <template #cell(refCode)="data">
            <b-badge
              variant="info"
              v-if="data.item.refCode"
              style="cursor: default">
              {{ data.item.refCode }}
            </b-badge>
          </template>
          <template #cell(projectCode)="data">
            <div
              v-if="data.item.project"
              v-b-tooltip.hover.bottom="{ variant: 'info' }"
              title="Candesic Project"
              class="selected-project"
              style="margin-bottom: 5px;"
              :style="
                'background-color: ' + data.item.project.color + ' !important'
              ">
              {{ data.item.project.title }}
            </div>
            <div
              v-else
              v-b-tooltip.hover.bottom="{ variant: 'info' }"
              title="Internal"
              class="selected-project"
              style="margin-bottom: 5px; background-color: #333;">
              CANDESIC
            </div>
          </template>
          <template #cell(owner)="data">
            <b-link
              @click="userChanged(data.item.owner)"
              style="color: #666 !important">
              {{ data.item.owner.fullName || '-' }}
            </b-link>
          </template>
          <template #cell(manager)="data">
            <span
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              title="Manager Assigned"
            >
              {{ (data.item.manager && data.item.manager.fullName) ? data.item.manager.fullName : '-' }}
            </span>
          </template>
          <template #cell(category)="data">
            <span v-if="data.item.fieldsOnEdit && data.item.fieldsOnEdit.includes('category')">
              <b-form-select
                size="sm"
                v-model="data.item.fee.type"
                :options="categoriesList"
                value-field="id"
                text-field="label"
              ></b-form-select>
            </span>
            <span
              v-else
              @click="enterEditMode(data.item, 'category')"
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              title="Click to modify"
              style="color: #666 !important; cursor: pointer">
              {{ data.item.fee.type || '-' }}
            </span>
          </template>
          <template #cell(amount)="data">
            <span v-if="data.item.fieldsOnEdit && data.item.fieldsOnEdit.includes('amount')">
              <b-form-input
                type="number"
                size="sm"
                step="1"
                v-model="data.item.fee.amount"
              ></b-form-input>
            </span>
            <span
              v-else
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              title="Click to modify"
              @click="enterEditMode(data.item, 'amount')"
              style="cursor: pointer"
            >{{ CurrencySymbol[data.item.fee.currency] }}{{ data.item.fee.amount ? parseFloat(data.item.fee.amount).toFixed(2) : '0.00' }}</span>
          </template>
          <template #cell(vat)="data">
            <span v-if="data.item.fieldsOnEdit && data.item.fieldsOnEdit.includes('vat')">
              <b-form-input
                type="number"
                size="sm"
                step="1"
                v-model="data.item.fee.vat"
              ></b-form-input>
            </span>
            <span
              v-else
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              title="Click to modify"
              @click="enterEditMode(data.item, 'vat')"
              style="cursor: pointer"
            >{{ CurrencySymbol[data.item.fee.currency] }}{{ data.item.fee.vat ? parseFloat(data.item.fee.vat).toFixed(2) : '0.00' }}</span>
          </template>
          <template #cell(notes)="data">
            <b-link
              v-b-popover.hover.top="data.item.notes"
              @click="toggleNotesModal(data.item)"
              :style="data.item.notes ? 'color: #333' : 'color: #999'"
              title="Click to change">
              {{ summarize(data.item.notes || '') || 'Click to add' }}
            </b-link>
          </template>
          <template #cell(updated)="data">
            {{ formatDate(data.item.updated) }}</template
          >
          <template #cell(created)="data">
            {{ formatDate(data.item.created) }}</template
          >
          <template #cell(expenseDate)="data">
            <span
              v-if="data.item.fieldsOnEdit && data.item.fieldsOnEdit.includes('expenseDate')"
              style="cursor: pointer">
              <el-date-picker
                type="date"
                size="small"
                value-format="yyyy-MM-dd"
                format="dd/MM/yyyy"
                :clearable="false"
                v-model="data.item.expenseDate"></el-date-picker>
            </span>
            <span
              v-else
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              title="Click to modify"
              @click="enterEditMode(data.item, 'expenseDate')"
              style="cursor: pointer"
              >{{ formatDate(data.item.expenseDate) }}</span>
          </template>
          <template #cell(attachments)="data">
            <ul class="lizard-attachment-list">
              <li v-if="data.item.attachments">
                <b-link @click="downloadReceipt(data.item.attachments)">
                  Download</b-link
                >
              </li>
              <li>
                <label class="attachment-upload-label" :class="{ disabled: data.item._uploading }">
                  {{ data.item._uploading ? 'Uploading…' : (data.item.attachments ? 'Replace' : 'Upload') }}
                  <input
                    type="file"
                    :disabled="data.item._uploading"
                    style="display: none"
                    @change="uploadBill($event, data.item)" />
                </label>
              </li>
            </ul>
          </template>
          <template #cell(status)="data">
            <b-form-select
              class="lizard-update-select"
              v-model="data.item.status"
              :options="expenseStatusOpts"
              @change="UpdateExpenseStatus(data.item)"
              size="sm"></b-form-select> </template
        ></b-table>
        <b-pagination
          v-if="Pager.pages > 1 && Pager.total > 0"
          v-model="Pager.page"
          :total-rows="Pager.total"
          :per-page="Pager.limit"
          @change="changePage"
          aria-controls="expensesTable"
          size="sm"
          pills
          align="center"></b-pagination>
      </div>
    </div>
    <div class="row" v-if="!loading && listItems.length && exportExcel">
      <div class="col-lg text-right">
        <b-button variant="outline-success" size="sm">
          <download-excel
            :before-generate="generateExcel"
            :before-finish="resetExportData"
            :type="exportType"
            :name="exportName"
            :fields="exportFields"
            :data="exportData"
            :worksheet="exportWorksheet">
            <b-icon
              icon="file-earmark-excel-fill"
              title="Export Excel"></b-icon>
            Export Excel
          </download-excel>
        </b-button>
      </div>
    </div>
    <b-modal
      v-b-modal.modal-footer-sm
      v-model="notesModal.show"
      centered
      :title="notesModal.data.title"
      :header-bg-variant="notesModal.headerBgVariant"
      :header-text-variant="notesModal.headerTextVariant"
      :body-bg-variant="notesModal.bodyBgVariant"
      :body-text-variant="notesModal.bodyTextVariant"
      :footer-bg-variant="notesModal.footerBgVariant"
      :footer-text-variant="notesModal.footerTextVariant">
      <b-container fluid>
        <b-row class="mb-1">
          <b-col>
            <b-textarea
              trim
              no-resize
              rows="4"
              v-model="notesModal.data.notesData.notes"
              placeholder="add your notes..."></b-textarea>
          </b-col>
        </b-row>
      </b-container>
      <template #modal-footer>
        <div class="w-100">
          <b-button
            variant="info"
            size="sm"
            class="float-right"
            @click="submitNotes">
            Submit
          </b-button>
        </div>
      </template>
    </b-modal>
    <b-overlay no-wrap :show="updateInProgress">
      <template #overlay>
        <div
          ref="dialog"
          tabindex="-1"
          role="dialog"
          aria-modal="false"
          aria-labelledby="form-confirm-label"
          class="p-3">
          <h3><strong style="color: #999">Change confirmation</strong></h3>
          <hr />
          <div class="d-flex text-left">
            {{ popupMsg }}
          </div>
          <hr />
          <div class="d-flex text-right">
            <b-button
              variant="secondary"
              @click="onCancel"
              style="margin-right: 3px !important">
              Cancel
            </b-button>
            <b-button variant="info" @click="onConfirm"> Confirm </b-button>
          </div>
        </div>
      </template>
    </b-overlay>
  </b-container>
</template>

<script>
import ServicesOld from '../../services/index.vue'
import Services from '../../services/main.service.vue'
import ReportsBox from './expense-components/reports-box.vue'
import FiltersBar from './expense-components/filters-bar.vue'
import { format as formatDate } from 'date-fns'

export default {
  name: 'ExpensesComponent',
  components: {
    ReportsBox,
    FiltersBar
  },
  data() {
    return {
      fieldsOnEdit: {},
      updateInProgress: false,
      updateQueue: [],
      popupMsg: '',
      notesModal: {
        show: false,
        headerBgVariant: 'info',
        headerTextVariant: 'light',
        bodyBgVariant: 'light',
        bodyTextVariant: 'dark',
        footerBgVariant: 'light',
        footerTextVariant: 'dark',
        data: {
          loading: true,
          title: '',
          notesData: { uid: null, notes: '' }
        }
      },
      loading: false,
      showReportsPanel: true,
      q: null,
      listFields: [
        { key: 'actions', label: '', sortable: false },
        { key: 'created', label: 'Submitted Date', sortable: false },
        // { key: 'refCode', label: 'Reference Code' },
        { key: 'expenseDate', label: 'Expense Date', sortable: false },
        { key: 'projectCode', label: 'Project' },
        { key: 'category', label: 'Category', sortable: false },
        { key: 'owner', label: 'Person name', sortable: false },
        { key: 'manager', label: 'Manager', sortable: false },
        { key: 'amount', label: 'Amount', sortable: false },
        { key: 'vat', label: 'VAT', sortable: false },
        { key: 'notes', label: 'Notes' },
        { key: 'attachments', label: 'Attachments' },
        { key: 'status', label: 'Status', sortable: false },
        { key: 'updated', label: 'Last Action Date', sortable: false }
      ],
      listItems: [],
      teamMembers: [],
      projectList: [],
      categoriesList: [],
      filters: {},
      rangeEnums: {
        dateLabel: ['Filter by Submitted Date', 'Filter by Expense Date'],
        months: [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December'
        ]
      },
      selectedItems: [],
      CurrencySymbol: {
        GBP: '£',
        USD: '$',
        EUR: '€'
      },
      expenseStatusOpts: [],
      exportExcel: true,
      exportType: 'xls',
      exportName: 'data.xls',
      exportWorksheet: "Candesic's Expenses Report",
      exportFields: {
        // No: 'no',
        Name: 'name',
        Email: 'email',
        Amount: 'amount',
        VAT: 'vat',
        Currency: 'currency',
        Manager: 'approvalManager',
        Category: 'category',
        Project: 'projectCode',
        Description: 'description',
        'Submitted Date': 'submitted',
        'Expense Date': 'date',
        Status: 'status',
        // 'Total GBP Amount (£)': 'gbpamount',
        'Approved Date': 'approvedDate',
        'Total to pay': 'total'
        // 'Paid Date': 'paidDate'
      },
      exportData: []
    }
  },
  computed: {
    Store() {
      return this.$store.getters
    },
    Pager() {
      return this.Store.expensePager
    },
    DateRange() {
      return this.Store.selectedExpenseDateRange
    },
    SelectedStatusTags() {
      return this.Store.selectedExpenseStatus
    }
  },
  mounted() {
    this.q = null
    setTimeout(() => {
      this.getExpenses()
      this.getUsers()
      this.getProjects()
      this.getCategories()
    }, 500)
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('keydown', this.handleGlobalKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('keydown', this.handleGlobalKeydown)
  },
  methods: {
    commitToStore(commitName, value) {
      this.$store.commit(commitName, value)
    },
    changePage(page = 1) {
      this.$store.commit('setExpensePager', { page })
    },
    async fetchMetas() {
      this.loading = true
      const { status, data } = await Services.Projects.Admin.getMetas()
      if (status === 200) {
        const {
          expenses: { status: expensesList }
        } = data.content.metas
        this.expenseStatusOpts = expensesList
      }
      this.loading = false
    },
    async getExpenses(filters = {}) {
      this.loading = true
      await this.fetchMetas()
      filters.status = this.SelectedStatusTags.length
        ? this.SelectedStatusTags.join(',')
        : this.expenseStatusOpts
            .map((itm) => itm.value)
            .slice(0, 1)
            .join(',')
      const { data, status } = await ServicesOld.GetExpenses(
        {
          page: this.Pager?.page ?? 1,
          ...filters
        },
        true
      )
      if (status === 200) {
        const { info, list } = data.content
        const { limit, page, pages, total, query } = info
        this.$store.commit('setExpensePager', { limit, page, pages, total })
        this.filters = this.filterExpenses(query)
        // this.$store.commit('setExpenseStatusOptions', this.filters.status)
        this.listItems = list.map((itm) => {
          this.fieldsOnEdit[itm.uid] = []
          itm.fieldsOnEdit = this.fieldsOnEdit[itm.uid]
          itm.status = itm.status.toUpperCase()
          itm._originalStatus = itm.status
          itm._uploading = false
          itm.manager = itm.manager || {}
          itm.manager.fullName = `${itm.manager?.firstName ?? ''} ${
            itm.manager?.lastName ?? ''
          }`.trim()
          itm.owner = itm.owner || {}
          itm.owner.fullName = `${itm.owner?.firstName ?? ''} ${
            itm.owner?.lastName ?? ''
          }`.trim()
          return itm
        })
      }
      this.loading = false
    },
    async getUsers() {
      const Users = await Services.Users.Admin.getList({ cache: true })
      if (Users.status === 200) {
        this.teamMembers = Users.data.content
          .filter((user) => {
            return user.settings.active
          })
          .map((user) => {
            return {
              id: user.uid,
              label: `${user.firstName} ${user.lastName}`
            }
          })
          .sort((a, b) => {
            if (a.label < b.label) {
              return -1
            }
            if (a.label > b.label) {
              return 1
            }
            return 0
          })
        this.teamMembers.unshift({
          id: 'all',
          label: 'All Team Members'
        })
      }
    },
    async getProjects() {
      const Projects = await Services.Projects.Admin.getProjects({
        cache: true
      })
      if (Projects.status === 200) {
        this.projectList = Projects.data.content
          .filter((project) => {
            return project.status === 'Live'
          })
          .map((project) => {
            return {
              id: project.uid,
              label: project.title
            }
          })
          .sort((a, b) => {
            if (a.label < b.label) {
              return -1
            }
            if (a.label > b.label) {
              return 1
            }
            return 0
          })
        this.projectList.unshift({
          id: 'CANDESIC',
          label: 'Candesic'
        })
        this.projectList.unshift({
          id: 'all',
          label: 'All Projects'
        })
      }
    },
    async getCategories() {
      const Categories = await ServicesOld.GetExpenseCategories({
        cache: true
      })
      if (Categories.status === 200) {
        this.categoriesList = Categories.data.content
          .map((category) => {
            return {
              id: category.value,
              label: category.name
            }
          })
          .sort((a, b) => {
            if (a.label < b.label) {
              return -1
            }
            if (a.label > b.label) {
              return 1
            }
            return 0
          })
        this.categoriesList.unshift({
          id: 'all',
          label: 'All Categories'
        })
      }
    },
    filterExpenses(data = {}) {
      const DataKeys = Object.keys(data)
      if (!DataKeys.length) {
        return {}
      }
      data.months =
        typeof data.months === 'string' ? data.months.split(',') : data.months
      return {
        dateLabel: this.rangeEnums.dateLabel[data.dateLabel],
        year: data.year,
        months: data.months.map((month) => {
          return this.rangeEnums.months[month]
        }),
        status: data.status.split(','),
        user: data?.user ?? null,
        project: data?.project ?? null,
        category: data?.category ?? null
      }
    },
    resetFilters() {
      this.filters.user = 'all'
      this.filters.project = 'all'
      this.filters.category = 'all'
      this.commitToStore('setSelectedExpenseDateRange', {
        user: 'all',
        project: 'all',
        category: 'all'
      })
      this.getExpenses({
        dateLabel: this.rangeEnums.dateLabel.findIndex(
          (label) => label === this.filters.dateLabel
        ),
        months: this.filters.months
          .map((month) =>
            this.rangeEnums.months.findIndex((mn) => mn === month)
          )
          .join(','),
        year: this.filters.year,
        user: null,
        project: null,
        category: null
      })
    },
    currencySymbol(currency) {
      let result = ''
      switch (currency.toLowerCase()) {
        case 'usd':
          result = '$'
          break
        case 'eur':
          result = '€'
          break
        default:
          result = '£'
      }
      return result
    },
    categoryLabel(cid) {
      return this.categories[cid] ?? 'n/a'
    },
    calcAmount({ currency, amount }) {
      let toGBP = ''
      return (
        this.currencySymbol(currency) + parseFloat(amount).toFixed(2) + toGBP
      )
    },
    GetExpenseStatusVariant(status = 'pending') {
      let variant = 'light'
      switch (status.toLowerCase()) {
        case 'approved':
          variant = 'info'
          break
        case 'rejected':
          variant = 'danger'
          break
        case 'pending':
          variant = 'warning'
          break
        case 'paid':
          variant = 'success'
          break
        case 'failed':
          variant = 'dark'
          break
      }
      return variant
    },
    async UpdateExpenseStatus(item) {
      const { uid, status } = item
      const previousStatus = item._originalStatus ?? status
      const bulkEditIsActive = this.selectedItems.length > 1
      try {
        if (bulkEditIsActive) {
          const response = await ServicesOld.UpdateBulkExpenseByAdmin(status, this.selectedItems)
          if (!response?.data?.status) {
            throw new Error('Bulk status update failed')
          }
          this.selectedItems.forEach((selectedUid) => {
            const itm = this.listItems.find((listItm) => listItm.uid === selectedUid)
            if (itm) {
              itm.status = status
              itm._originalStatus = status
            }
          })
          this.selectedItems = []
          this.$refs.expensesTable.clearSelected()
        } else {
          const response = await ServicesOld.UpdateBulkExpenseByAdmin(status, [uid])
          if (!response?.data?.status) {
            throw new Error('Status update failed')
          }
          item._originalStatus = status
        }
      } catch (Exception) {
        console.error(Exception.message)
        item.status = previousStatus
        this.$bvToast.toast('Failed to update status.', { title: 'Error', variant: 'danger', solid: true })
      }
    },
    async downloadReceipt(fileName) {
      if (fileName === null) {
        return
      }
      return await ServicesOld.DownloadExpenseBill(fileName)
    },
    // Remove is not wired up yet: the admin update endpoint can't clear
    // attachmentKeys (candesic/portal-service#165), so only upload/replace
    // is supported from this screen for now.
    async uploadBill(ev, item) {
      const file = ev.target.files[0]
      if (!file) {
        return
      }
      item._uploading = true
      try {
        const { data, status } = await ServicesOld.UploadExpenseBill(file)
        if (status !== 200) {
          throw new Error('Failed to upload attachment')
        }
        const fileName = data.content.fileName
        const response = await ServicesOld.UpdateExpenseByAdmin(item.uid, {
          attachmentKeys: fileName
        })
        if (!response?.data?.status) {
          throw new Error('Failed to save attachment')
        }
        item.attachments = fileName
      } catch (err) {
        console.error(err.message)
        this.$bvToast.toast('Failed to upload attachment.', { title: 'Error', variant: 'danger', solid: true })
      } finally {
        item._uploading = false
        ev.target.value = ''
      }
    },
    rowsSelected(rows) {
      this.selectedItems = []
      rows.forEach((itm) => {
        this.selectedItems.push(itm.uid)
      })
    },
    summarize(note) {
      if (!note) {
        return ''
      }
      note = note.trim()
      if (note.length > 20) {
        return note.substring(0, 20) + '...'
      } else {
        return note
      }
    },
    userChanged(user = null) {
      // pass user uid to store
      this.commitToStore('setSelectedExpenseDateRange', {
        user: user?.id ?? user?.uid ?? 'all'
      })
    },
    projectChanged(project = null) {
      // pass project uid to store
      this.commitToStore('setSelectedExpenseDateRange', {
        project: project?.id ?? project?.uid ?? 'all'
      })
    },
    categoryChanged(category = null) {
      // pass project uid to store
      this.commitToStore('setSelectedExpenseDateRange', {
        category: category?.id ?? category?.uid ?? 'all'
      })
    },
    async generateExcel() {
      try {
        this.exportName = `expenses-report.${this.exportType}`
        this.exportData = []

        const filters = { ...this.filters }
        filters.status = this.SelectedStatusTags.length
          ? this.SelectedStatusTags.join(',')
          : this.expenseStatusOpts
              .map((itm) => itm.value)
              .slice(0, 1)
              .join(',')
        const { status, data } = await ServicesOld.GetExpenseExcelReports({
          dateLabel: this.rangeEnums.dateLabel.findIndex(
            (label) => label === filters.dateLabel
          ),
          months: filters.months
            .map((month) =>
              this.rangeEnums.months.findIndex((mn) => mn === month)
            )
            .join(','),
          year: filters.year,
          status: filters.status,
          user: filters?.user ?? null,
          project: filters?.project ?? null,
          category: filters?.category ?? null
        })
        this.exportData = status === 200 ? data.content : []
      } catch (Exception) {
        console.log('ERRROR"')
      }
    },
    resetExportData() {
      this.exportName = `data.${this.exportType}`
      this.exportData = []
    },
    formatDate(dt) {
      if (!dt) return '-'
      return formatDate(new Date(dt), 'dd/MM/yyyy')
    },
    enterEditMode(item, field) {
      // Enforce single-row editing by rolling back any other active rows
      Object.keys(this.fieldsOnEdit).forEach(uid => {
         if (uid !== item.uid && this.fieldsOnEdit[uid] && this.fieldsOnEdit[uid].length > 0) {
            const otherItem = this.listItems.find(i => i.uid === uid)
            if (otherItem) this.cancelRowEdits(otherItem)
         }
      })

      const fieldsOnEdit = this.fieldsOnEdit[item.uid] || []
      if (!fieldsOnEdit.includes(field)) {
        item._originalValues = item._originalValues || {}
        if (['category', 'amount', 'vat'].includes(field)) {
          item._originalValues[field] = item.fee[field === 'category' ? 'type' : field]
          
          if (field === 'category') {
             const matchedCat = this.categoriesList.find(c => c.label === item.fee.type)
             if (matchedCat) {
               item.fee.type = matchedCat.id
             }
          }
        } else {
          item._originalValues[field] = item[field]
        }
        fieldsOnEdit.push(field)
        this.fieldsOnEdit[item.uid] = fieldsOnEdit
        item.fieldsOnEdit = this.fieldsOnEdit[item.uid]
        this.fieldsOnEdit = { ...this.fieldsOnEdit }
      }
    },
    cancelRowEdits(item) {
      const fields = this.fieldsOnEdit[item.uid] || []
      if (fields.length > 0) {
        if (item._originalValues) {
          fields.forEach(f => {
            if (item._originalValues[f] !== undefined) {
              if (['category', 'amount', 'vat'].includes(f)) {
                 item.fee[f === 'category' ? 'type' : f] = item._originalValues[f]
              } else {
                 item[f] = item._originalValues[f]
              }
            }
          })
        }
        item.fieldsOnEdit.splice(0, item.fieldsOnEdit.length)
        this.fieldsOnEdit[item.uid] = item.fieldsOnEdit
        this.fieldsOnEdit = { ...this.fieldsOnEdit }
      }
    },
    saveRowEdits(item) {
       let payload = {}
       let hasCritical = false
       const fields = this.fieldsOnEdit[item.uid] || []
       if (fields.length === 0) return

       fields.forEach(field => {
          if (['amount', 'vat', 'expenseDate'].includes(field)) hasCritical = true
           
          if (field === 'category') {
            payload.fee = payload.fee || { ...item.fee }
            payload.fee.type = item.fee.type
          } else if (['amount', 'vat'].includes(field)) {
            payload.fee = payload.fee || { ...item.fee }
            payload.fee[field] = item.fee[field]
          } else {
            payload[field] = item[field]
          }
       });

       item.fieldsOnEdit.splice(0, item.fieldsOnEdit.length)
       this.fieldsOnEdit[item.uid] = item.fieldsOnEdit
       this.fieldsOnEdit = { ...this.fieldsOnEdit }

       if (hasCritical) {
          this.popupMsg = 'You are going to change critical fields for this expense.'
          item._pendingPayload = payload
          item._pendingFields = [...fields] 
          this.updateQueue = [item]
          this.updateInProgress = true
       } else {
          this.triggerExpenseFieldUpdate(item.uid, payload)
       }
    },
    onCancel() {
      this.popupMsg = ''
      this.updateInProgress = false
      const item = this.updateQueue[0]
      if (item && item._pendingFields) {
         item._pendingFields.forEach(f => {
            if (item._originalValues && item._originalValues[f] !== undefined) {
               if (['category', 'amount', 'vat'].includes(f)) {
                  item.fee[f === 'category' ? 'type' : f] = item._originalValues[f]
               } else {
                  item[f] = item._originalValues[f]
               }
            }
         })
      }
      this.updateQueue = []
    },
    onConfirm() {
      const item = this.updateQueue[0]
      this.triggerExpenseFieldUpdate(item.uid, item._pendingPayload)
      this.popupMsg = ''
      this.updateInProgress = false
      this.updateQueue = []
    },
    async triggerExpenseFieldUpdate(uid, payload) {
      try {
        const response = await ServicesOld.UpdateExpenseByAdmin(uid, payload)
        if (!response.data.status) {
           this.$bvToast.toast('Failed to update field.', { title: 'Error', variant: 'danger', solid: true })
           this.getExpenses()
        }
      } catch (err) {
        console.error(err)
        this.$bvToast.toast('Failed to update field.', { title: 'Error', variant: 'danger', solid: true })
        this.getExpenses()
      }
    },
    toggleNotesModal(itm = null) {
      this.notesModal.data.loading = true
      this.notesModal.data.notesData = {}
      if (!this.notesModal.show && itm) {
        this.notesModal.data.title = 'Loading...'
        this.notesModal.data.notesData = {
          uid: itm.uid,
          notes: itm.notes ?? ''
        }
        this.notesModal.data.title = `Notes for ${itm.owner.fullName}'s expense`
      }
      this.notesModal.data.loading = false
      this.notesModal.show = !this.notesModal.show
    },
    async submitNotes() {
      const { uid, notes } = this.notesModal.data.notesData
      const itemIndex = this.listItems.findIndex((item) => item.uid === uid)
      const response = await ServicesOld.UpdateExpenseByAdmin(uid, { notes })
      if (response.data.status) {
        if(itemIndex > -1) {
            this.listItems[itemIndex].notes = notes
        }
      } else {
        this.$bvToast.toast('Please try again later', { title: 'Error', variant: 'danger', solid: true })
      }
      this.toggleNotesModal()
    }
  },
  watch: {
    DateRange: {
      handler(val) {
        const keys = Object.keys(val)
        if (!keys.length) {
          return
        }
        if (JSON.stringify(val) !== JSON.stringify(this.filters)) {
          this.getExpenses({
            dateLabel: this.rangeEnums.dateLabel.findIndex(
              (label) => label === val.dateLabel
            ),
            months: val.months
              .map((month) =>
                this.rangeEnums.months.findIndex((mn) => mn === month)
              )
              .join(','),
            year: val.year,
            user: val?.user ?? null,
            project: val?.project ?? null,
            category: val?.category ?? null
          })
        }
      },
      immediate: true
    },
    SelectedStatusTags: {
      handler() {
        if (Object.keys(this.filters).length) {
          this.getExpenses({
            dateLabel: this.rangeEnums.dateLabel.findIndex(
              (label) => label === this.DateRange.dateLabel
            ),
            months: this.DateRange.months
              .map((month) =>
                this.rangeEnums.months.findIndex((mn) => mn === month)
              )
              .join(','),
            year: this.DateRange.year,
            user: this.DateRange?.user ?? null,
            project: this.DateRange?.project ?? null,
            category: this.DateRange?.category ?? null
          })
        }
      },
      immediate: true
    },
    Pager: {
      handler(nxt, prv) {
        const prevPage = prv?.page ?? 1
        const nextPage = nxt?.page ?? 1
        if (prevPage !== nextPage && Object.keys(this.filters).length) {
          this.getExpenses({
            dateLabel: this.rangeEnums.dateLabel.findIndex(
              (label) => label === this.DateRange.dateLabel
            ),
            months: this.DateRange.months
              .map((month) =>
                this.rangeEnums.months.findIndex((mn) => mn === month)
              )
              .join(','),
            year: this.DateRange.year,
            user: this.DateRange?.user ?? null,
            project: this.DateRange?.project ?? null,
            category: this.DateRange?.category ?? null
          })
        }
      },
      immediate: true
    }
  }
}
</script>

<style>
.cost-details-btn {
  font-size: 9pt !important;
  font-weight: bold;
  color: #6cf;
}
.lizard-column-fees {
  font-family: 'Courier New', Courier, monospace !important;
  font-size: 12px !important;
  font-weight: 100 !important;
}
.lizard-column-fees-disabled {
  color: #999;
}
.custom-link {
  color: '#999' !important;
}
.custom-link:hover {
  color: '#666' !important;
  text-decoration: underline !important;
}
.attachment-upload-label {
  cursor: pointer;
  color: #666;
  font-weight: normal;
  margin: 0;
}
.attachment-upload-label.disabled {
  color: #999;
  cursor: default;
}
</style>

<style scoped>
.lizard-expense-status {
  font-variant: small-caps;
  font-weight: bolder;
  font-size: 12pt;
  letter-spacing: 0.03em;
  padding: 0 8px 4px 8px;
  margin: 0 auto;
}
.lizard-expense-status:hover {
  cursor: pointer;
}
.export-excel-button {
  font-size: 10pt;
  font-weight: bold;
  text-align: center;
  border: 2px solid rgb(40, 128, 5);
  border-radius: 2px;
  background-color: transparent;
  color: rgb(40, 128, 5);
  margin: 10px 0;
  padding: 2px;
  width: 10%;
  max-width: 10%;
  float: right;
}
.export-excel-button:hover {
  background-color: rgb(40, 128, 5, 0.6);
  color: #fff;
  cursor: pointer;
}
.export-excel-button:active {
  background-color: rgb(40, 128, 5, 0.8);
  color: #ccc;
}
.lizard-activity-list {
  font-size: 12px !important;
  color: #666;
  font-weight: bold !important;
}
.lizard-column-index {
  text-align: center !important;
}
.lizard-form-empty-col {
  min-height: 20px !important;
}
.lizard-reports-stats li {
  display: inline-block;
}
.lizard-hourly-rate-input {
  text-align: center;
  width: 60px;
  max-width: 60px;
}
.lizard-date-input {
  text-align: center;
  width: 90px;
  max-width: 90px;
}
.header-msg {
  color: #666;
  font-weight: bold !important;
}
label {
  color: #666;
  line-height: 40px !important;
  font-weight: bold !important;
  font-size: 12px !important;
}
.txt {
  color: #666;
  font-weight: bold !important;
  font-size: 12px !important;
}
</style>
